import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/FormField.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Money } from '../../../components/ui/Money.js';
import { useToast } from '../../../components/ui/Toast.js';
import { Search, ArrowUpRight, Clock, Plus, X } from 'lucide-react';

import {
  useRunningReceipts,
  useBounceRunningReceipt,
  useClearRunningReceipt,
  useCreateRunningReceipt,
  useCustomerGroupBalances,
  useSubscriptionLateFees,
} from '../../../features/finance/hooks.js';
import { useCustomers } from '../../../features/customers/hooks.js';
import type { CustomerListItem } from '../../../features/customers/types.js';
import { useDebounce } from '../../../lib/hooks.js';

interface LateFee {
  id: string;
  feeAmountPaise: number;
  month?: string;
  accrualDate?: string;
}

interface GroupBalance {
  subscriberId: string;
  schemeId?: string;
  schemeNumber: string;
  schemeName?: string;
  ticketNumber: number;
  runningAmountPaise: number;
}

import { TableSkeleton } from '../../../components/ui/Skeleton.js';
import { EmptyState } from '../../../components/ui/EmptyState.js';
import { usePageTitle } from '../../../lib/hooks.js';
import { PageHeader } from '../../../components/layout/PageHeader.js';
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerBody,
  DrawerFooter,
} from '../../../components/ui/Drawer.js';
import { FormField } from '../../../components/ui/FormField.js';
import { FormSelect } from '../../../components/ui/FormSelect.js';
import { AccountSearchInput } from '../../../features/accounting/components/AccountSearchInput.js';
import Decimal from 'decimal.js';
import { z } from 'zod';

export const Route = createFileRoute('/_auth/finance/running-receipts')({
  component: RunningReceiptsPage,
});

interface ReceiptRecord {
  id: string;
  receiptNumber: string;
  customerId: string;
  entryType: 'group' | 'others';
  amountPaise: number;
  paymentMode: 'CASH' | 'CHEQUE' | 'UPI' | 'NEFT';
  status: 'PENDING' | 'CLEARED' | 'BOUNCED';
  receiptDate: string;
  instrumentType: string;
  notes?: string;
}

const runningReceiptBaseSchema = z.object({
  customerId: z.string().uuid('Customer selection is required'),
  transactionType: z.enum(['credit', 'debit']),
  amountPaise: z.number().int().positive('Amount must be positive'),
  instrumentType: z.enum(['CASH', 'CHEQUE', 'UPI', 'NEFT']),
  instrumentNumber: z.string().optional(),
  agentCode: z.string().optional(),
  receiptDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Valid date required'),
  notes: z.string().min(1, 'Notes are required'),
  sendSms: z.boolean(),
  entryType: z.enum(['group', 'others']),
  chitGroupId: z.string().uuid().optional(),
  subscriptionId: z.string().uuid().optional(),
  lateFeeEntryIds: z.array(z.string().uuid()).optional(),
  paymentAccountId: z.string().uuid().optional(),
}).superRefine((data, ctx) => {
  if (data.entryType === 'group') {
    if (!data.chitGroupId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Chit scheme is required for group entries', path: ['chitGroupId'] });
    }
    if (!data.subscriptionId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Subscription is required for group entries', path: ['subscriptionId'] });
    }
  }
  if (data.instrumentType !== 'CASH' && !data.instrumentNumber) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Instrument number is required for non-cash payments', path: ['instrumentNumber'] });
  }
});

function CreateRunningReceiptDrawer({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const createMutation = useCreateRunningReceipt();

  // Customer selection
  const [customerQuery, setCustomerQuery] = React.useState('');
  const debouncedQuery = useDebounce(customerQuery, 300);
  const { data: customersResponse, isLoading: searchingCustomers } = useCustomers({
    page: 1,
    limit: 5,
    query: debouncedQuery || undefined,
  });
  const [selectedCustomer, setSelectedCustomer] = React.useState<CustomerListItem | null>(null);

  // Load customer balances
  const { data: groupBalancesResponse } = useCustomerGroupBalances(selectedCustomer?.id);
  const groupBalances = (groupBalancesResponse?.data ?? []) as unknown as GroupBalance[];

  // Form fields
  const [entryType, setEntryType] = React.useState<'group' | 'others'>('others');
  const [subscriptionId, setSubscriptionId] = React.useState('');
  const [transactionType, setTransactionType] = React.useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = React.useState('');
  const [instrumentType, setInstrumentType] = React.useState('CHEQUE');
  const [instrumentNumber, setInstrumentNumber] = React.useState('');
  const [agentCode, setAgentCode] = React.useState('');
  const [receiptDate, setReceiptDate] = React.useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = React.useState('');
  const [sendSms, setSendSms] = React.useState(false);
  const [paymentAccountId, setPaymentAccountId] = React.useState('');

  // Late fees checked state
  const { data: lateFeesResponse } = useSubscriptionLateFees(subscriptionId);
  const lateFees = (lateFeesResponse?.data ?? []) as unknown as LateFee[];
  const [checkedLateFees, setCheckedLateFees] = React.useState<Record<string, boolean>>({});

  // Auto-fill late fee amount
  const selectedLateFeeSum = React.useMemo(() => {
    let sum = 0;
    lateFees.forEach((fee) => {
      if (checkedLateFees[fee.id]) {
        sum += fee.feeAmountPaise;
      }
    });
    return sum;
  }, [checkedLateFees, lateFees]);

  React.useEffect(() => {
    if (selectedLateFeeSum > 0) {
      setAmount(new Decimal(selectedLateFeeSum).dividedBy(100).toString());
    }
  }, [selectedLateFeeSum]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCustomer) {
      toast({ type: 'error', title: 'Error', description: 'Please select a customer.' });
      return;
    }

    let decimalAmount: Decimal;
    try {
      decimalAmount = new Decimal(amount);
    // allow-silent-catch: user input validation failure is handled via toast
    } catch {
      toast({ type: 'error', title: 'Error', description: 'Please enter a valid amount.' });
      return;
    }

    if (decimalAmount.lte(0)) {
      toast({ type: 'error', title: 'Error', description: 'Please enter a valid amount.' });
      return;
    }

    const payload = {
      customerId: selectedCustomer.id,
      entryType,
      transactionType,
      amountPaise: new Decimal(amount).times(100).toNumber(),
      instrumentType,
      instrumentNumber: instrumentType !== 'CASH' ? instrumentNumber : undefined,
      agentCode: agentCode || undefined,
      receiptDate,
      notes,
      sendSms,
      chitGroupId: entryType === 'group' ? groupBalances.find((b) => b.subscriberId === subscriptionId)?.schemeId : undefined,
      subscriptionId: entryType === 'group' ? subscriptionId : undefined,
      lateFeeEntryIds: entryType === 'group' && transactionType === 'credit' ? Object.keys(checkedLateFees).filter(id => checkedLateFees[id]) : [],
      paymentAccountId: paymentAccountId || undefined,
    };

    const validationResult = runningReceiptBaseSchema.safeParse(payload);
    if (!validationResult.success) {
      const firstIssue = validationResult.error.issues[0];
      toast({ type: 'error', title: 'Validation Error', description: firstIssue?.message || 'Invalid form data.' });
      return;
    }

    createMutation.mutate(payload, {
      onSuccess: () => {
        toast({ type: 'success', title: 'Success', description: 'Running receipt successfully logged!' });
        setOpen(false);
        onCreated();
        // Reset form
        setSelectedCustomer(null);
        setCustomerQuery('');
        setAmount('');
        setNotes('');
        setInstrumentNumber('');
        setAgentCode('');
        setCheckedLateFees({});
        setPaymentAccountId('');
      },
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to create running receipt.';
        toast({ type: 'error', title: 'Error', description: message });
      }
    });
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button variant="primary" size="sm" className="flex items-center gap-1">
          <Plus className="w-4 h-4" /> Log Running Receipt
        </Button>
      </DrawerTrigger>
      <DrawerContent className="w-full max-w-2xl bg-surface-1">
        <DrawerHeader>
          <DrawerTitle>Log Running Receipt Voucher</DrawerTitle>
          <DrawerClose asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Close">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <DrawerBody className="space-y-6">
          <form id="running-receipt-form" onSubmit={handleSubmit} className="space-y-6 pb-20">
            {/* Customer Search */}
            <FormField label="Search Customer" required>
              <div className="relative">
                <Input
                  value={customerQuery}
                  onChange={(e) => {
                    setCustomerQuery(e.target.value);
                    if (selectedCustomer) {
                      setSelectedCustomer(null);
                      setSubscriptionId('');
                    }
                  }}
                  placeholder="Type customer name, phone, or code..."
                  className="w-full"
                />
                {searchingCustomers && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">
                    Searching...
                  </span>
                )}
              </div>

              {customerQuery && !selectedCustomer && customersResponse && (
                <div className="mt-1 max-h-40 overflow-y-auto bg-surface-3 border border-border rounded-lg divide-y divide-border shadow-lg">
                  {customersResponse.data.length === 0 ? (
                    <div className="p-3 text-center text-xs text-text-muted">
                      No active customers found
                    </div>
                  ) : (
                    customersResponse.data.map((c) => (
                      <Button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedCustomer(c);
                          setCustomerQuery(c.name);
                        }}
                        className="w-full text-left p-3 hover:bg-surface-2 transition-colors flex flex-col text-xs bg-surface-1 border-0 rounded-none shadow-none text-text-primary"
                      >
                        <span className="font-semibold text-text-primary">{c.name}</span>
                        <span className="text-text-muted mt-0.5">
                          Phone: {c.mobile} | Code: {c.customerCode}
                        </span>
                      </Button>
                    ))
                  )}
                </div>
              )}
            </FormField>

            {selectedCustomer && (
              <div className="p-4 bg-surface-2 border border-border rounded-lg text-xs grid grid-cols-2 gap-4">
                <div>
                  <p className="font-bold text-text-primary mb-1">Customer Profile</p>
                  <p className="text-text-secondary">Name: {selectedCustomer.name}</p>
                  <p className="text-text-secondary">Phone: {selectedCustomer.mobile}</p>
                  <p className="text-text-secondary">Code: {selectedCustomer.customerCode}</p>
                </div>
                {entryType === 'group' && groupBalances.length > 0 && (
                  <div>
                    <p className="font-bold text-text-primary mb-1">Active Chits Summary</p>
                    {groupBalances.map((gb) => (
                      <p key={gb.subscriberId} className="text-text-secondary">
                        {gb.schemeNumber} (Tkt {gb.ticketNumber}): Balance <Money paise={gb.runningAmountPaise} />
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Entry Type */}
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                label="Entry Type"
                value={entryType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setEntryType(e.target.value as 'group' | 'others');
                  setSubscriptionId('');
                }}
                options={[
                  { value: 'others', label: 'Others Entry (General Voucher)' },
                  { value: 'group', label: 'Group Entry (Chit Fund)' },
                ]}
                required
              />

              <FormSelect
                label="Transaction Direction"
                value={transactionType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTransactionType(e.target.value as 'credit' | 'debit')}
                options={[
                  { value: 'credit', label: 'Credit (Receipt)' },
                  { value: 'debit', label: 'Debit (Ad-hoc Payment)' },
                ]}
                required
              />
            </div>

            {/* Chit Subscription Dropdown */}
            {entryType === 'group' && selectedCustomer && (
              <FormSelect
                label="Select Subscriber Ticket"
                value={subscriptionId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSubscriptionId(e.target.value)}
                options={[
                  { value: '', label: '-- Select Ticket --' },
                  ...groupBalances.map((gb) => ({
                    value: gb.subscriberId,
                    label: `${gb.schemeName} [${gb.schemeNumber}] (Tkt: ${gb.ticketNumber})`,
                  })),
                ]}
                required
              />
            )}

            {/* Late Fees List */}
            {entryType === 'group' && transactionType === 'credit' && subscriptionId && lateFees.length > 0 && (
              <div className="space-y-2 border border-border p-4 rounded-lg bg-surface-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-text-primary">Outstanding Late Fees</span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="text-xs h-7 py-0 px-2"
                    onClick={() => {
                      const allChecked: Record<string, boolean> = {};
                      lateFees.forEach((fee) => {
                        allChecked[fee.id] = true;
                      });
                      setCheckedLateFees(allChecked);
                    }}
                  >
                    Select All
                  </Button>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {lateFees.map((fee) => (
                    <label key={fee.id} className="flex items-center justify-between text-xs p-2 hover:bg-surface-3 rounded cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Input
                          type="checkbox"
                          checked={!!checkedLateFees[fee.id]}
                          onChange={(e) => setCheckedLateFees({ ...checkedLateFees, [fee.id]: e.target.checked })}
                          className="rounded border-border text-accent-500 focus:ring-accent-500 h-4 w-4"
                        />
                        <span>Inst. Month {fee.month} (Accrual: {fee.accrualDate})</span>
                      </div>
                      <span className="font-bold text-danger-text"><Money paise={fee.feeAmountPaise} /></span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Amount & Mode */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Amount (INR)" required>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full"
                  required
                />
              </FormField>

              <FormSelect
                label="Payment Instrument"
                value={instrumentType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setInstrumentType(e.target.value)}
                options={[
                  { value: 'CHEQUE', label: 'Cheque' },
                  { value: 'CASH', label: 'Cash' },
                  { value: 'UPI', label: 'UPI' },
                  { value: 'NEFT', label: 'NEFT' },
                ]}
                required
              />
            </div>

            {/* Instrument Details */}
            {instrumentType !== 'CASH' && (
              <FormField label="Instrument / Reference Number" required>
                <Input
                  type="text"
                  value={instrumentNumber}
                  onChange={(e) => setInstrumentNumber(e.target.value)}
                  placeholder="e.g. Cheque # or UPI Tx ID"
                  className="w-full"
                  required
                />
              </FormField>
            )}

            {/* Agent, Date & SMS */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Agent Serial / Code">
                <Input
                  type="text"
                  value={agentCode}
                  onChange={(e) => setAgentCode(e.target.value)}
                  placeholder="e.g. AGT-2026-0001"
                  className="w-full"
                />
              </FormField>

              <FormField label="Receipt Date" required>
                <Input
                  type="date"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  className="w-full"
                  required
                />
              </FormField>
            </div>

            {/* Payment Account */}
            <FormField
              label="Cash / Bank Account"
              hint="Selecting an account posts this receipt to the general ledger immediately. Leave blank to log the receipt without a ledger posting (e.g. a reconciliation-only entry)."
            >
              <AccountSearchInput
                value={paymentAccountId}
                onChange={setPaymentAccountId}
                placeholder="Search cash/bank account..."
              />
            </FormField>

            {/* Particular / Notes */}
            <FormField label="Particulars / Notes" required>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Log transaction details..."
                className="w-full h-20 p-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
                required
              />
            </FormField>

            <label className="flex items-center gap-2 cursor-pointer">
              <Input
                type="checkbox"
                checked={sendSms}
                onChange={(e) => setSendSms(e.target.checked)}
                className="rounded border-border text-accent-500 focus:ring-accent-500 h-4 w-4"
              />
              <span className="text-xs font-semibold text-text-secondary">Send SMS confirmation receipt</span>
            </label>
          </form>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="running-receipt-form"
            variant="primary"
            size="sm"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Saving...' : 'Submit Receipt'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function RunningReceiptsPage() {
  usePageTitle('Running Receipts');
  const { toast } = useToast();
  const [query, setQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');

  const { data: response, isLoading, isError, error, refetch } = useRunningReceipts();
  const receipts = (response?.data ?? []) as ReceiptRecord[];

  const bounceMutation = useBounceRunningReceipt();
  const clearMutation = useClearRunningReceipt();

  const handleBounceCheque = (id: string) => {
    bounceMutation.mutate(id, {
      onSuccess: () => {
        toast({
          type: 'success',
          title: 'Cheque Marked Bounced',
          description: 'The cheque instrument status has been updated to BOUNCED. Reverse ledger feeds have been dispatched.',
        });
        refetch();
      },
      onError: (err: unknown) => {
        toast({
          type: 'error',
          title: 'Failed to Mark Bounced',
          description: (err as { message?: string })?.message || 'Could not mark the instrument as bounced. Please try again.',
        });
      },
    });
  };

  const handleClearCheque = (id: string) => {
    clearMutation.mutate(id, {
      onSuccess: () => {
        toast({
          type: 'success',
          title: 'Receipt Cleared',
          description: 'The instrument has cleared successfully. No further ledger action is needed -- the receipt was already posted to the ledger when it was recorded.',
        });
        refetch();
      },
      onError: (err: unknown) => {
        toast({
          type: 'error',
          title: 'Failed to Clear Receipt',
          description: (err as { message?: string })?.message || 'Could not clear the instrument. Please try again.',
        });
      },
    });
  };

  const filteredReceipts = receipts.filter((r: ReceiptRecord) => {
    const matchesQuery = r.receiptNumber.toLowerCase().includes(query.toLowerCase()) ||
      r.customerId.toLowerCase().includes(query.toLowerCase());

    const matchesStatus = !statusFilter || r.status === statusFilter;
    return matchesQuery && matchesStatus;
  });

  const totalPaiseCleared = receipts.filter((r: ReceiptRecord) => r.status === 'CLEARED').reduce((acc: number, r: ReceiptRecord) => acc + r.amountPaise, 0);
  const totalPaisePending = receipts.filter((r: ReceiptRecord) => r.status === 'PENDING').reduce((acc: number, r: ReceiptRecord) => acc + r.amountPaise, 0);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Running Receipts Register"
        subtitle="Real-time feed of all physical and digital subscription payments logged across all branches and agents."
        action={<CreateRunningReceiptDrawer onCreated={refetch} />}
      />
      <div className="mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
            <Input
              type="text"
              placeholder="Search receipt #, subscriber..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
            >
              <option value="">All Statuses</option>
              <option value="CLEARED">Cleared</option>
              <option value="PENDING">Pending</option>
              <option value="BOUNCED">Bounced</option>
            </select>
          </div>
        </div>
      </div>
      <div className="flex-1 w-full min-h-0 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Total Cleared Receipts</span>
                <h2 className="text-2xl font-bold text-success-text">
                  <Money paise={totalPaiseCleared} />
                </h2>
              </div>
              <div className="p-3 bg-success-bg text-success-500 rounded-lg">
                <ArrowUpRight className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Uncleared Instruments (In-Transit)</span>
                <h2 className="text-2xl font-bold text-warning-text">
                  <Money paise={totalPaisePending} />
                </h2>
              </div>
              <div className="p-3 bg-warning-bg text-warning-text rounded-lg">
                <Clock className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <TableSkeleton columns={8} rows={5} />
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load receipts</p>
              <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : filteredReceipts.length === 0 ? (
            <EmptyState
              title="No receipts found"
              description={query || statusFilter ? 'No records match your filters.' : 'No receipts have been logged yet.'}
              icon={<ArrowUpRight className="w-8 h-8" />}
            />
          ) : (
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Receipt No.</th>
                  <th className="p-4">Type</th>
                  <th className="p-4">Amount Paid</th>
                  <th className="p-4">Payment Mode</th>
                  <th className="p-4">Receipt Date</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredReceipts.map((r: ReceiptRecord) => (
                  <tr key={r.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4 font-mono text-xs font-semibold">{r.receiptNumber}</td>
                    <td className="p-4 font-bold capitalize">{r.entryType}</td>
                    <td className="p-4">
                      <Money paise={r.amountPaise} />
                    </td>
                    <td className="p-4 font-semibold text-xs text-text-secondary">{r.instrumentType}</td>
                    <td className="p-4 font-mono text-xs">
                      {new Date(r.receiptDate).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="p-4">
                      <StatusBadge
                        status={r.status === 'CLEARED' ? 'success' : r.status === 'BOUNCED' ? 'danger' : 'warning'}
                        label={r.status}
                      />
                    </td>
                    <td className="p-4 text-right">
                      {r.status === 'PENDING' && (
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-success-500 hover:text-success-text font-semibold h-8 py-0 px-2"
                            onClick={() => handleClearCheque(r.id)}
                          >
                            Clear
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-danger-500 hover:text-danger-700 font-semibold h-8 py-0 px-2"
                            onClick={() => handleBounceCheque(r.id)}
                          >
                            Bounce
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}